import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Loader2, Camera, KeyRound, CreditCard, X, Wand2 } from 'lucide-react';

type Profile = {
  display_name: string | null;
  avatar_url: string | null;
  email: string;
  is_subscribed?: boolean;
  stripe_customer_id?: string;
};

export default function Settings() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  

  // AI avatar generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY as string | undefined;
  const geminiImageModel = (import.meta.env.VITE_GEMINI_IMAGE_MODEL as string | undefined) || 'gemini-2.5-flash-image-preview';

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('display_name, avatar_url, email, is_subscribed, stripe_customer_id')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return;
    }

    setProfile(data);
    setDisplayName(data.display_name || '');
    setIsSubscribed(data.is_subscribed || false);
  };

  

  // --- AI Avatar Helpers ---
  async function callGeminiImage(prompt: string): Promise<{ base64: string; mime: string }> {
    if (!geminiApiKey) throw new Error('Missing Gemini API key. Set VITE_GEMINI_API_KEY in your env.');
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(geminiImageModel)}:generateContent`;

    // Download the two reference images and convert to base64
    const img1Url = 'https://i.ibb.co/p6DWFR4h/Screenshot-2025-09-05-at-2-51-36-AM.png';
    const img2Url = 'https://i.ibb.co/8gb0VG2B/Generated-Image-September-04-2025-12-17-PM.jpg';
    const [img1, img2] = await Promise.all([fetchAsBase64(img1Url), fetchAsBase64(img2Url)]);

    // Hardcoded template + user-controlled color/action/pose
    const composedText = `This is what user asked to build\n` +
      `User prompt: "${prompt}"\n` +
      `Use the stuff from my monster prompt, and keep the same background and the hoodie as the reference pictures, ALWAYS. ` +
      `Always keep the monsters fluffy (soft, furry texture) — never smooth or scaly. ` +
      `Generate the character like I have described in my actual prompt; only use the color and the action/pose from the user prompt.`;

    const body = {
      contents: [
        {
          parts: [
            { text: composedText },
            { inline_data: { mime_type: img1.mime, data: img1.base64 } },
            { inline_data: { mime_type: img2.mime, data: img2.base64 } },
          ],
        },
      ],
      generationConfig: {
        temperature: 0.6,
        topP: 0.9,
        topK: 32,
        candidateCount: 1,
        seed: 1234,
        stopSequences: [],
      },
      safetySettings: [],
    } as const;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': geminiApiKey,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`Gemini image error ${res.status}: ${txt}`);
    }
    const json = await res.json();
    const parts: any[] = json?.candidates?.[0]?.content?.parts ?? [];
    // Find the first part that contains inline image data
    let inline: any | undefined;
    for (const p of parts) {
      if (p?.inlineData || p?.inline_data) {
        inline = p.inlineData || p.inline_data;
        break;
      }
    }

    let data: string | undefined = inline?.data;
    let mime: string = inline?.mimeType || 'image/png';

    // Fallback: some responses might attach data directly on a part
    if (!data && parts.length) {
      const withData = parts.find((p: any) => typeof p?.data === 'string');
      if (withData) {
        data = withData.data;
        mime = withData?.mimeType || mime;
      }
    }

    // Last resort: check if inlineData exists on content directly
    if (!data) {
      const fallbackInline = json?.candidates?.[0]?.content?.inlineData || json?.candidates?.[0]?.content?.inline_data;
      if (fallbackInline?.data) {
        data = fallbackInline.data;
        mime = fallbackInline?.mimeType || mime;
      }
    }

    if (!data) {
      throw new Error('No image returned from Gemini');
    }
    return { base64: data, mime };
  }

  function dataUrlToImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = dataUrl;
    });
  }

  async function blobToBase64(blob: Blob): Promise<{ base64: string; mime: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const idx = result.indexOf('base64,');
        if (idx === -1) {
          return reject(new Error('Failed to parse data URL'));
        }
        const base64 = result.slice(idx + 7);
        const mimeMatch = result.match(/^data:(.*?);base64,/);
        const mime = mimeMatch?.[1] || 'application/octet-stream';
        resolve({ base64, mime });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  async function fetchAsBase64(url: string): Promise<{ base64: string; mime: string }> {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`Failed to download image: ${url}`);
    const blob = await res.blob();
    return blobToBase64(blob);
  }

  async function resizeToSquare(base64: string, mime: string, size = 400): Promise<Blob> {
    const dataUrl = `data:${mime};base64,${base64}`;
    const img = await dataUrlToImage(dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');

    // Cover fit: scale and center-crop to square
    const iw = img.width;
    const ih = img.height;
    const scale = Math.max(size / iw, size / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (size - dw) / 2;
    const dy = (size - dh) / 2;
    ctx.drawImage(img, dx, dy, dw, dh);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('Failed to encode image'));
        resolve(blob);
      }, mime, mime === 'image/jpeg' ? 0.9 : undefined);
    });
  }

  const handleGenerateAvatar = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) return;
    setAiError(null);
    setAiGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // 1) Generate image
      const { base64, mime } = await callGeminiImage(prompt);
      // 2) Resize to 400x400 (preserve mime type)
      const blob = await resizeToSquare(base64, mime, 400);
      // 3) Upload to Supabase Storage (avatars) using File. Use a unique name to force INSERT (RLS allows INSERT but not UPDATE on avatars)
      const ext = mime === 'image/jpeg' ? 'jpg' : mime === 'image/png' ? 'png' : 'png';
      const unique = Date.now();
      const fileName = `${user.id}-${unique}.${ext}`;
      const file = new File([blob], fileName, { type: mime });
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: false });
      if (uploadError) throw uploadError;

      // 4) Get public URL and update profile
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(fileName);
      const publicUrl = urlData.publicUrl;
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);
      if (updateError) throw updateError;

      setProfile((prev) => (prev ? { ...prev, avatar_url: publicUrl } : prev));
      // Cleanup local state and refresh page per request
      setAiPrompt('');
      setTimeout(() => {
        try {
          window.location.reload();
        } catch {}
      }, 300);
    } catch (e: any) {
      console.error('AI avatar error:', e);
      setAiError(e?.message || 'Failed to generate image');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      alert('Image size must be less than 2MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Only image files are allowed');
      return;
    }

    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      console.error('Error uploading avatar:', uploadError);
      setLoading(false);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', user.id);

    if (updateError) {
      console.error('Error updating profile:', updateError);
    } else {
      setProfile(prev => prev ? { ...prev, avatar_url: publicUrl } : null);
    }

    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('profiles')
      .update({ display_name: displayName })
      .eq('id', user.id);

    if (error) {
      console.error('Error updating profile:', error);
    } else {
      setProfile(prev => prev ? { ...prev, display_name: displayName } : null);
    }

    setSaving(false);
  };

  const handleCancelClick = () => {
    // Show the cancellation modal
    setShowCancelModal(true);
    setCancelReason('');
    setOtherReason('');
  };
  
  const handleManageSubClick = async () => {
    // Directly redirect to subscription management portal
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await handleManageSubscription(user.id);
    }
  };
  
  const handleCloseModal = () => {
    setShowCancelModal(false);
  };
  
  const handleSubmitFeedback = async () => {
    setSubmittingFeedback(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('You must be logged in to cancel your subscription');
      }
      
      // Store the cancellation reason in database
      const { error: feedbackError } = await supabase
        .from('leaving_reason')
        .insert({
          user_id: user.id,
          reason: cancelReason,
          remarks: cancelReason === 'Too Expensive' || cancelReason === 'other' ? otherReason : null
        });
      
      if (feedbackError) throw feedbackError;
      
      // Now proceed with the subscription cancellation
      await handleManageSubscription(user.id);
      
    } catch (err) {
      console.error('Feedback submission error:', err);
      if (typeof err === 'object' && err !== null && 'message' in err) {
        setSubscriptionError((err as any).message);
      } else {
        setSubscriptionError('An unexpected error occurred');
      }
    } finally {
      setSubmittingFeedback(false);
      setShowCancelModal(false); // Close the modal
    }
  };
  
  const handleManageSubscription = async (userId: string) => {
    setLoadingSubscription(true);
    setSubscriptionError(null);
    
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      // Call the Supabase Edge Function to create a portal session
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { 
          user_id: userId
        }
      });
      
      if (error) throw error;
      
      // Redirect to Stripe customer portal
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create portal session');
      }
      
    } catch (err) {
      console.error('Subscription portal error:', err);
      if (typeof err === 'object' && err !== null && 'message' in err) {
        setSubscriptionError((err as any).message);
      } else {
        setSubscriptionError('An unexpected error occurred');
      }
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    setChangingPassword(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: profile?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        setPasswordError(signInError.message);
        setChangingPassword(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError('Failed to update password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="py-6 px-2 sm:p-8 min-h-screen relative">
      {/* Page heading with subtle neon effect */}
      <h1 className="text-3xl font-bold text-white mb-10 relative inline-block">
        Profile Settings
        <div className="absolute -bottom-2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#c9fffc] to-transparent opacity-70"></div>
      </h1>

      {/* Grid layout for settings sections - staggered layout with different heights */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-min">
        {/* Profile Card */}
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-6 border border-[#c9fffc]/10 shadow-xl hover:shadow-[#c9fffc]/10 transition-all duration-500 overflow-hidden relative group lg:col-span-1 md:row-span-1">
          {/* Subtle glow effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#c9fffc]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          {/* Top right corner neon accent */}
          <div className="absolute -top-10 -right-10 w-20 h-20 bg-[#c9fffc]/10 rounded-full blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          <div className="mb-6 flex flex-col items-center relative z-10">
          <div className="relative mb-4">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-700">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <Camera className="w-8 h-8" />
                </div>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              ref={fileInputRef}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="absolute bottom-0 right-0 bg-[#c9fffc] text-gray-900 rounded-full p-2 hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="text-sm text-gray-400">
            Click the camera icon to change your profile photo
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-1">
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
              placeholder="Enter your display name"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Save Changes'
            )}
          </button>
        </form>

        {/* AI Profile Photo Generator (moved inside Profile card) */}
        <div className="mt-8 pt-5 border-t border-[#c9fffc]/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <Wand2 className="w-6 h-6 text-[#c9fffc] relative z-10" />
              <div className="absolute inset-0 bg-[#c9fffc]/20 rounded-full blur-sm animate-pulse"></div>
            </div>
            <h2 className="text-lg font-semibold text-white">AI Profile Photo</h2>
          </div>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-300">Describe your avatar</label>
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. teal; waving hello"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
            />
            {aiError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-sm text-red-400">{aiError}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleGenerateAvatar}
              disabled={aiGenerating || !aiPrompt.trim()}
              className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {aiGenerating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
              {aiGenerating ? 'Generating...' : 'Generate with AI'}
            </button>
            
          </div>
        </div>
        </div>

        {/* Password Change Card */}
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-6 border border-[#c9fffc]/10 shadow-xl hover:shadow-[#c9fffc]/10 transition-all duration-500 overflow-hidden relative group lg:col-span-1 md:row-span-2">
          {/* Subtle glow effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#c9fffc]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          {/* Bottom left corner neon accent */}
          <div className="absolute -bottom-10 -left-10 w-20 h-20 bg-[#c9fffc]/10 rounded-full blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="flex items-center gap-3 mb-6 pb-2 border-b border-[#c9fffc]/20">
            <div className="relative">
              <KeyRound className="w-6 h-6 text-[#c9fffc] relative z-10" />
              <div className="absolute inset-0 bg-[#c9fffc]/20 rounded-full blur-sm animate-pulse"></div>
            </div>
            <h2 className="text-lg font-semibold text-white">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
                placeholder="Enter your current password"
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-1">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
                placeholder="Enter your new password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
                placeholder="Confirm your new password"
              />
            </div>

            {passwordError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-sm text-red-400">{passwordError}</p>
              </div>
            )}

            {passwordSuccess && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <p className="text-sm text-green-400">Password updated successfully!</p>
              </div>
            )}

            <button
              type="submit"
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
            >
              {changingPassword ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                'Change Password'
              )}
            </button>
          </form>
        </div>

        {/* Subscription Management Card */}
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-6 border border-[#c9fffc]/10 shadow-xl hover:shadow-[#c9fffc]/10 transition-all duration-500 overflow-hidden relative group lg:col-span-1 md:row-span-1">
          {/* Subtle glow effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#c9fffc]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          {/* Top left corner neon accent */}
          <div className="absolute -top-10 -left-10 w-20 h-20 bg-[#c9fffc]/10 rounded-full blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="flex items-center gap-3 mb-6 pb-2 border-b border-[#c9fffc]/20">
            <div className="relative">
              <CreditCard className="w-6 h-6 text-[#c9fffc] relative z-10" />
              <div className="absolute inset-0 bg-[#c9fffc]/20 rounded-full blur-sm animate-pulse"></div>
            </div>
            <h2 className="text-lg font-semibold text-white">Manage Subscription</h2>
          </div>
          
          <div className="mb-4">
            <div className="p-3 bg-gray-700/50 rounded-lg mb-4">
              <p className="text-gray-300">
                {isSubscribed 
                  ? "You currently have an active subscription. You can manage your billing information, view invoices, or cancel your subscription." 
                  : "You don't have an active subscription. Subscribe to access all features."}
              </p>
              <div className="mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#c9fffc]/10 text-[#c9fffc] border border-[#c9fffc]/20">
                  Status: {isSubscribed ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {subscriptionError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-400">{subscriptionError}</p>
              </div>
            )}

            <div className="space-y-3">
              <button
                type="button"
                onClick={handleCancelClick}
                disabled={loadingSubscription || !profile?.stripe_customer_id}
                className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
              >
                {loadingSubscription ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  'Cancel Subscription'
                )}
              </button>
              
              <button
                type="button"
                onClick={handleManageSubClick}
                disabled={loadingSubscription || !profile?.stripe_customer_id}
                className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
              >
                {loadingSubscription ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  'Manage Subscription'
                )}
              </button>
            </div>
            
            {!profile?.stripe_customer_id && (
              <p className="text-sm text-gray-400 mt-2 text-center">
                You need to subscribe first before you can manage your subscription.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl max-w-md w-full border border-gray-700 shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-semibold text-white">We're sorry to see you go</h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-300 mb-5">
                Before you head out, could you let us know what is the reason for leaving?
              </p>
              
              <div className="space-y-3 mb-5">
                {['Too Expensive', 'Not Enough Classes', 'Not Enough Blueprints', 'other'].map((reason) => (
                  <div key={reason} className="flex items-start">
                    <input
                      type="radio"
                      id={reason}
                      name="cancelReason"
                      value={reason}
                      checked={cancelReason === reason}
                      onChange={() => setCancelReason(reason)}
                      className="mt-1 mr-3"
                    />
                    <label htmlFor={reason} className="text-gray-300 cursor-pointer">
                      {reason}
                    </label>
                  </div>
                ))}
              </div>
              
              {(cancelReason === 'Too Expensive' || cancelReason === 'other') && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {cancelReason === 'Too Expensive' 
                      ? 'What could we have done to make it more valuable?' 
                      : 'Please tell us more'}
                  </label>
                  <textarea
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc] min-h-[80px]"
                    placeholder="Your feedback helps us improve"
                  />
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitFeedback}
                  disabled={!cancelReason || (cancelReason === 'other' && !otherReason) || submittingFeedback}
                  className="flex-1 px-4 py-2 bg-[#c9fffc] text-gray-900 rounded-lg hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50 flex justify-center items-center"
                >
                  {submittingFeedback ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Continue to Cancellation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}